/**
 * Zoom-based city visibility configuration.
 *
 * At lower zoom levels (more zoomed out), only major world cities are shown.
 * As zoom increases, more cities appear based on their scalerank.
 *
 * Natural Earth scalerank: 0 = most important (mega-cities like Tokyo, NYC),
 * higher values = less important cities.
 */

import type { CityData } from '../types/features'

/**
 * Maximum scalerank to show at each zoom level.
 * Cities with scalerank <= threshold will be shown.
 *
 * At zoom 3: Mega-cities (scalerank 0)
 * At zoom 4: Major cities (scalerank 0-1)
 * At zoom 5: Large cities (scalerank 0-3)
 * At zoom 6+: More cities progressively
 */
const CITY_SCALERANK_THRESHOLDS: Record<number, number> = {
  3: 0,   // Mega-cities only (Tokyo, NYC, London, etc.)
  4: 1,   // Major world cities
  5: 3,   // Large cities (capitals + major regional)
  6: 4,   // Medium-large cities
  7: 5,   // Medium cities
  8: 5,   // Smaller cities
  9: 5,   // All loaded cities
}

/**
 * Minimum zoom level for city labels.
 */
export const CITY_MIN_ZOOM = 3

/**
 * Filter cities based on zoom level.
 * Returns cities that should be visible at the given zoom.
 */
export function filterCitiesByZoom(cities: CityData[], zoom: number): CityData[] {
  // Clamp zoom to our threshold range
  const effectiveZoom = Math.min(Math.max(CITY_MIN_ZOOM, Math.floor(zoom)), 9)
  const maxScalerank = CITY_SCALERANK_THRESHOLDS[effectiveZoom] ?? 5

  return cities.filter((city) => city.scalerank <= maxScalerank)
}

/**
 * Get the scalerank threshold for a given zoom level.
 * Useful for debugging.
 */
export function getCityScalerankThreshold(zoom: number): number {
  const effectiveZoom = Math.min(Math.max(CITY_MIN_ZOOM, Math.floor(zoom)), 9)
  return CITY_SCALERANK_THRESHOLDS[effectiveZoom] ?? 5
}
